import React, {useCallback, useState} from "react"
import {useTranslation} from "react-i18next"
import {Navigate, useNavigate, useParams} from "react-router-dom"
import {useConfig} from "../../hooks/useConfig"
import {useBoothSession} from "../../hooks/useBoothSession"
import {processAiEffect} from "../../library/requests/AiRequests"
import {Button, FormControl, InputLabel, MenuItem, Paper, Select, Stack, TextField} from "@mui/material"
import {AiEffectSequenceGuestPickerInputResource} from "../../library/models/AiEffectSequenceResource"
import Spinner from "../common/Spinner"
import {ArrowRightAltOutlined} from "@mui/icons-material"

function GuestInputPage() {
  const params = useParams<"code">()
  const code = params.code!
  const navigate = useNavigate()
  const {t} = useTranslation()

  const {config, loading} = useConfig(code)
  const {rawImage, setGuestInputValues, addAiImage} = useBoothSession(config?.presetCode ?? code)

  const guestInputs = config?.aiEffectSequence?._embedded.guestInputs ?? []
  const sortedInputs = [...guestInputs].sort((a, b) => a.order - b.order)

  const [values, setValues] = useState<Record<string, string>>({})

  const [processing, setProcessing] = useState(false)

  const handleChange = (key: string, value: string) => {
    setValues(prev => ({...prev, [key]: value}))
  }

  const handleSubmit = useCallback(async () => {
    if (!config || !rawImage) return

    setGuestInputValues(values)

    setProcessing(true)
    processAiEffect(config.presetCode, rawImage, values)
      .then((imageUrl) => addAiImage(imageUrl))
      .finally(() => navigate(`/${config.presetCode}/edit`))

  }, [config, rawImage, values, setGuestInputValues, addAiImage, navigate])

  if (loading || !config) {
    return null
  }

  if (!rawImage) {
    return <Navigate to={`/${config.presetCode}/camera`} replace/>
  }

  const theme = config.theme

  if (processing) {
    return (
      <div className="mt-5" style={{textAlign: "center"}}>
        <p style={{...theme.textStyle, fontSize: "1.5rem"}}>{t("guestInput.processing")}</p>
        <Spinner color={theme.textStyle.color}/>
      </div>
    )
  }

  return (
    <Paper
      component={Stack}
      spacing={2}
      sx={{
        p: 2,
        maxWidth: 600,
        margin: "0 auto",
        mt: 6,
        boxShadow: "0px 8px 10px RGBA(0, 0, 0, 0.25)",
      }}>
      <h2 style={{color: "#333", marginBottom: "1.5rem"}}>{t("guestInput.heading")}</h2>

      {sortedInputs.map(input => {
        const key = input.promptPlaceholder

        if (input.type === "TEXT") {
          return (
            <TextField
              key={key}
              label={input.label}
              value={values[key] ?? ""}
              onChange={e => handleChange(key, e.target.value)}
              helperText={input.defaultValue ? t("guestInput.example", {value: input.defaultValue}) : undefined}
              fullWidth
              margin="normal"
            />
          )
        }

        const pickerInput = input as AiEffectSequenceGuestPickerInputResource
        const sortedOptions = [...pickerInput._embedded.options].sort((a, b) => a.order - b.order)

        return (
          <FormControl key={key} fullWidth margin="normal">
            <InputLabel>{input.label}</InputLabel>
            <Select
              label={input.label}
              value={values[key] ?? ""}
              onChange={e => handleChange(key, e.target.value as string)}
              MenuProps={{style: {zIndex: 9999}}}
            >
              {sortedOptions.map(option => (
                <MenuItem key={option.id} value={option.value}>
                  {option.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      })}

      <Button
        onClick={handleSubmit}
        variant="contained"
        endIcon={<ArrowRightAltOutlined/>}
        fullWidth
        disabled={sortedInputs.some(input => !values[input.promptPlaceholder]?.trim())}
      >
        {t("guestInput.submit")}
      </Button>
    </Paper>
  )
}

export default GuestInputPage
